import Amount from "../model/common/Amount";
import QuoteItem from "../model/quote/QuoteItem";
import Big from "big.js";
import {none, option, Option} from "ts-option";

// This is a file copied from the mobile application
// Any changes should be applied to both projects

export interface QuoteCalculatorInputData {
	markup: number,
	vat: number,
	vatRegistered: boolean,
	items: QuoteItem[],
	maxFee: number
}

export interface QuoteCalculatorOutputData {
	labourTotalCost: Amount,
	totalPrice: Amount,
	totalPriceBeforeVat: Amount,
	totalVat: Option<Amount>,
	markupAmount: Amount,
	hiberCommission: Amount,
	installerGross: Amount,
	totalMargin: Amount,
}

export class QuoteCalculator {

	static calculateFromLabourCost(labourTotalCost: Amount, data: QuoteCalculatorInputData): QuoteCalculatorOutputData {
		const {
			markup,
			vat,
			vatRegistered,
			items,
			maxFee
		} = data;
		const markupAsFraction = this.getValueAsFraction(markup);
		const vatAsFraction = this.getValueAsFraction(vat);

		//these are constant values on Quote
		const hiberParts = items.filter(i => !i.selfSourced);
		const hiberPartsCost = this.calculatePartsPrice(hiberParts, true);
		const hiberPartsMarkup = this.calculatePartsMarkup(hiberParts, true);
		const partnerBrandMarkupOnAsset = hiberPartsCost.multiple(markupAsFraction);
		let totalMarkup = new Amount(Math.min(hiberPartsMarkup.add(partnerBrandMarkupOnAsset).cents, maxFee));

		const totalMarkupVat = totalMarkup.multiple(vatAsFraction);
		const partsVat = this.calculatePartsVat(items, true, vat);
		const selfSourcedPartsCost = this.calculatePartsPrice(items.filter(i => i.selfSourced), true)


		let totalPriceBeforeVat;
		let totalPrice;
		let totalVat;

		const partsCost = hiberPartsCost.add(selfSourcedPartsCost)

		if (vatRegistered) {
			const labourVat = labourTotalCost.multiple(vatAsFraction);
			const totalVatValue = partsVat.add(totalMarkupVat).add(labourVat);
			totalPriceBeforeVat = partsCost.add(totalMarkup).add(labourTotalCost);
			totalPrice = totalPriceBeforeVat.add(totalVatValue)
			totalVat = option(totalVatValue);
		}
		else {
			const totalVatValue = partsVat.add(totalMarkupVat);
			totalPriceBeforeVat = partsCost.add(totalMarkup).add(labourTotalCost).add(totalVatValue);
			totalPrice = totalPriceBeforeVat;
			totalMarkup = totalMarkup.add(totalMarkupVat);
			totalVat = none;
		}

		return {
			labourTotalCost,
			totalPrice,
			totalPriceBeforeVat,
			totalVat,
			markupAmount: totalMarkup,
			hiberCommission: totalMarkup,
			installerGross: labourTotalCost.add(selfSourcedPartsCost),
			totalMargin: totalMarkup
		}
	}

	static calculateFromTotal(totalPrice: Amount, data: QuoteCalculatorInputData): QuoteCalculatorOutputData {
		const {
			markup,
			vat,
			vatRegistered,
			items,
			maxFee
		} = data;
		let labourTotalCost: Amount;

		const markupAsFraction = this.getValueAsFraction(markup);
		const vatAsFraction = this.getValueAsFraction(vat);

		//these are constant values on Quote
		const hiberParts = items.filter(i => !i.selfSourced);
		const hiberPartsCost = this.calculatePartsPrice(hiberParts, true);
		const hiberPartsMarkup = this.calculatePartsMarkup(hiberParts, true);
		const partnerBrandMarkupOnAsset = hiberPartsCost.multiple(markupAsFraction);
		let totalMarkup = new Amount(Math.min(hiberPartsMarkup.add(partnerBrandMarkupOnAsset).cents, maxFee));

		const totalMarkupVat = totalMarkup.multiple(vatAsFraction);
		const partsVat = this.calculatePartsVat(items, true, vat);
		const selfSourcedPartsCost = this.calculatePartsPrice(items.filter(i => i.selfSourced), true)

		let totalPriceBeforeVat;
		let totalVat;

		const partsCost = hiberPartsCost.add(selfSourcedPartsCost)

		if (vatRegistered) {
			const labourTotalCostWithVat = totalPrice.subtract(partsCost.add(totalMarkup).add(partsVat.add(totalMarkupVat)));
			labourTotalCost = labourTotalCostWithVat.getValueBeforePercentage(vat);
			const labourVat = labourTotalCostWithVat.subtract(labourTotalCost);
			const totalVatValue = partsVat.add(totalMarkupVat).add(labourVat);
			totalPriceBeforeVat = partsCost.add(totalMarkup).add(labourTotalCost);
			totalVat = option(totalVatValue);
		}
		else {
			const totalVatValue = partsVat.add(totalMarkupVat);
			labourTotalCost = totalPrice.subtract(partsCost.add(totalMarkup).add(totalVatValue));
			totalPriceBeforeVat = partsCost.add(totalMarkup).add(labourTotalCost).add(totalVatValue);
			totalMarkup = totalMarkup.add(totalMarkupVat);
			totalVat = none;
		}

		return {
			labourTotalCost,
			totalPrice,
			totalPriceBeforeVat,
			totalVat,
			markupAmount: totalMarkup,
			hiberCommission: totalMarkup,
			installerGross: labourTotalCost.add(selfSourcedPartsCost),
			totalMargin: totalMarkup
		}
	}

	static calculatePartsPrice(items: QuoteItem[], vatRegistered: boolean) {
		return items.reduce((sum, i) => sum.add(i.getNetPrice(vatRegistered)), new Amount(0))
	}

	static calculatePartsVat(items: QuoteItem[], vatRegistered: boolean, vat: number) {
		if (vatRegistered) {
			return items.reduce((sum, i) => sum.add(i.getVatPrice(vatRegistered, vat)), new Amount(0))
		}
		return new Amount(0);
	}

	static calculatePartsMarkup(items: QuoteItem[], vatRegistered: boolean) {
		return items.reduce((sum, i) => sum.add(i.getMarkup(vatRegistered)), new Amount(0));
	}

	static getValueAsFraction(value: number): number {
		return Number(new Big(value).div(100).toFixed(4));
	}
}
